import { ReactNode, CSSProperties } from 'react';
import cn from 'classnames';
import Tooltip, { ITooltipProps } from '@creditflowresearch/uilib/Tooltip';
import styles from './truncateText.module.scss';

export interface IProps {
  /**
   * The value associated with the component
   */
  value: string,
  /**
   * The child elements to be rendered inside the component
   */
  children?: ReactNode,
  /**
   * The number of lines to be displayed
   */
  linesCount?: number,
  /**
   * Additional class name for the component.
   */
  className?: string,
  /**
   * Defines whether the tooltip should always be shown.
   */
  shouldShowTooltip?: ITooltipProps['shouldShowTooltip'] | boolean,
}

const singleLineShouldShowTooltip: ITooltipProps['shouldShowTooltip'] = ({ container }) => (
  container.scrollWidth > container.clientWidth
);

const multiLineShouldShowTooltip: ITooltipProps['shouldShowTooltip'] = ({ container }) => (
  container.scrollHeight > container.clientHeight
);

const TruncateText = ({
  value,
  children,
  linesCount = 1,
  className,
  shouldShowTooltip,
}: IProps) => {
  const isSingleLine = linesCount === 1;

  const defaultHandler = isSingleLine ? singleLineShouldShowTooltip : multiLineShouldShowTooltip;

  const shouldShowTooltipHandler: ITooltipProps['shouldShowTooltip'] = (options) => {
    if (typeof shouldShowTooltip === 'boolean') {
      return shouldShowTooltip;
    }

    return (shouldShowTooltip ?? defaultHandler)(options);
  };

  return (
    <span className={ cn(styles.wrapper, className) }>
      <Tooltip
        className={ styles.tooltip }
        containerClassName={ cn(
          styles.tooltipContainer,
          isSingleLine ? styles.singleLine : styles.multiLine,
        ) }
        containerStyles={ !isSingleLine ? { '--lines-count': linesCount } as CSSProperties : undefined }
        content={ value }
        shouldShowTooltip={ shouldShowTooltipHandler }
        persistInDom
      >
        { children || value }
      </Tooltip>
    </span>
  );
};

export default TruncateText;
